import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import DateFilter from './DateFilter';
import {
  Users,
  TrendingUp,
  Activity,
  BarChart3,
  Calendar,
  Flame,
  Snowflake,
  Scissors,
  CircleDot,
  FolderOpen,
  Play,
  CheckCircle,
  FileText,
  Wrench,
  Layers
} from 'lucide-react';

const PerformanceReports = ({ userRole }) => {
  const { toast } = useToast();
  const [operatorReport, setOperatorReport] = useState(null);
  const [coordinatorReport, setCoordinatorReport] = useState(null);
  const [cuttingByMachine, setCuttingByMachine] = useState(null);
  const [pressingByType, setPressingByType] = useState(null);
  const [cuttingHeatmap, setCuttingHeatmap] = useState(null);
  const [coordTasksReport, setCoordTasksReport] = useState(null);
  const [supervisorReport, setSupervisorReport] = useState(null);
  const [period, setPeriod] = useState('current');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('coordinators');

  useEffect(() => {
    fetchReports();
  }, [period, customStartDate, customEndDate]);

  const computePeriodDates = () => {
    // Mirrors backend "period" logic for ad-hoc machine report endpoints
    if (period === 'custom') {
      return [
        customStartDate ? customStartDate.toISOString() : null,
        customEndDate ? customEndDate.toISOString() : null
      ];
    }
    const now = new Date();
    if (period === 'today') {
      const s = new Date(now); s.setHours(0, 0, 0, 0);
      return [s.toISOString(), now.toISOString()];
    }
    if (period === 'yesterday') {
      const s = new Date(now); s.setDate(s.getDate() - 1); s.setHours(0, 0, 0, 0);
      const e = new Date(now); e.setDate(e.getDate() - 1); e.setHours(23, 59, 59, 999);
      return [s.toISOString(), e.toISOString()];
    }
    if (period === 'weekly') {
      const s = new Date(now); s.setDate(s.getDate() - 7);
      return [s.toISOString(), now.toISOString()];
    }
    if (period === 'monthly') {
      const s = new Date(now); s.setDate(s.getDate() - 30);
      return [s.toISOString(), now.toISOString()];
    }
    return [null, null];
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const startDateStr = customStartDate ? customStartDate.toISOString() : null;
      const endDateStr = customEndDate ? customEndDate.toISOString() : null;
      const [machineStart, machineEnd] = computePeriodDates();
      
      // Fetch operator report
      const opReport = await reportsAPI.getOperatorPerformance(period, null, startDateStr, endDateStr);
      setOperatorReport(opReport);
      
      // Fetch coordinator report (owner only)
      if (userRole === 'owner' || userRole === 'admin') {
        const coordReport = await reportsAPI.getCoordinatorPerformance(period, null, startDateStr, endDateStr);
        setCoordinatorReport(coordReport);
      }

      // Fetch machine-grouped reports
      try {
        const [cm, pt, hm, ct, sr] = await Promise.all([
          reportsAPI.getCuttingByMachine(machineStart, machineEnd),
          reportsAPI.getPressingByMachineType(machineStart, machineEnd),
          reportsAPI.getCuttingMachineHeatmap(machineStart, machineEnd),
          reportsAPI.getCoordinatorTasks(machineStart, machineEnd),
          reportsAPI.getSupervisors(period, startDateStr, endDateStr).catch(() => null)
        ]);
        setCuttingByMachine(cm);
        setPressingByType(pt);
        setCuttingHeatmap(hm);
        setCoordTasksReport(ct);
        setSupervisorReport(sr);
      } catch (err) {
        // non-fatal — keep existing reports visible
        setCuttingByMachine(null);
        setPressingByType(null);
        setCuttingHeatmap(null);
        setCoordTasksReport(null);
        setSupervisorReport(null);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load reports", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (start, end) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod === 'all' ? 'current' : newPeriod);
    if (newPeriod !== 'custom') {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'weekly': return 'Last 7 Days';
      case 'monthly': return 'Last 30 Days';
      case 'custom': return 'Custom Range';
      default: return 'All Time';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'machine_operator': return 'bg-blue-100 text-blue-800';
      case 'cutting_operator': return 'bg-purple-100 text-purple-800';
      case 'boring_operator': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'machine_operator': return 'Machine Op';
      case 'cutting_operator': return 'Cutting Op';
      case 'boring_operator': return 'Boring Op';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-reports">
      {/* Header with period selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Performance Reports
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            {getPeriodLabel()}
            {operatorReport?.start_date && (
              <span className="ml-2">
                ({new Date(operatorReport.start_date).toLocaleDateString()} - {new Date(operatorReport.end_date).toLocaleDateString()})
              </span>
            )}
          </p>
        </div>
        
        <DateFilter
          value={period === 'current' ? 'all' : period}
          onChange={handlePeriodChange}
          startDate={customStartDate}
          endDate={customEndDate}
          onDateRangeChange={handleDateRangeChange}
        />
      </div>

      {/* Tabs for Owner to switch between operators and coordinators */}
      {(userRole === 'owner' || userRole === 'admin') ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap h-auto w-full justify-start p-1.5 gap-1 bg-slate-100 rounded-xl">
            <TabsTrigger
              value="coordinators"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-violet-50 data-[state=active]:bg-violet-100 data-[state=active]:text-violet-700 data-[state=active]:shadow-sm"
              data-testid="coordinators-tab"
            >
              <TrendingUp className="w-4 h-4 text-violet-500" />
              Coordinators
            </TabsTrigger>
            <TabsTrigger
              value="operators"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-blue-50 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700 data-[state=active]:shadow-sm"
              data-testid="operators-tab"
            >
              <Users className="w-4 h-4 text-blue-500" />
              Operators
            </TabsTrigger>
            <TabsTrigger
              value="cutting-machines"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-amber-50 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700 data-[state=active]:shadow-sm"
              data-testid="cutting-machines-tab"
            >
              <Wrench className="w-4 h-4 text-amber-500" />
              Cutting by Machine
            </TabsTrigger>
            <TabsTrigger
              value="cutting-heatmap"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-rose-50 data-[state=active]:bg-rose-100 data-[state=active]:text-rose-700 data-[state=active]:shadow-sm"
              data-testid="cutting-heatmap-tab"
            >
              <Activity className="w-4 h-4 text-rose-500" />
              Machine Heatmap
            </TabsTrigger>
            <TabsTrigger
              value="pressing-types"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-orange-50 data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700 data-[state=active]:shadow-sm"
              data-testid="pressing-types-tab"
            >
              <Layers className="w-4 h-4 text-orange-500" />
              Pressing HOT/COLD
            </TabsTrigger>
            <TabsTrigger
              value="coord-tasks"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-emerald-50 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm"
              data-testid="coord-tasks-tab"
            >
              <Users className="w-4 h-4 text-emerald-500" />
              Coordinator Tasks
            </TabsTrigger>
            <TabsTrigger
              value="supervisors"
              className="flex items-center gap-2 rounded-lg py-2 px-3 font-medium text-slate-500 transition-all hover:bg-purple-50 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 data-[state=active]:shadow-sm"
              data-testid="supervisors-tab"
            >
              <CheckCircle className="w-4 h-4 text-purple-500" />
              Supervisors
            </TabsTrigger>
          </TabsList>

          <TabsContent value="operators" className="mt-4">
            <OperatorPerformanceTable operators={operatorReport?.operators || []} getRoleBadgeColor={getRoleBadgeColor} getRoleLabel={getRoleLabel} />
          </TabsContent>

          <TabsContent value="coordinators" className="mt-4">
            <CoordinatorPerformanceTable coordinators={coordinatorReport?.coordinators || []} />
          </TabsContent>

          <TabsContent value="cutting-machines" className="mt-4">
            <CuttingByMachineReport data={cuttingByMachine} />
          </TabsContent>

          <TabsContent value="cutting-heatmap" className="mt-4">
            <CuttingMachineHeatmap data={cuttingHeatmap} />
          </TabsContent>

          <TabsContent value="pressing-types" className="mt-4">
            <PressingByTypeReport data={pressingByType} />
          </TabsContent>

          <TabsContent value="coord-tasks" className="mt-4">
            <CoordinatorTasksReport data={coordTasksReport} />
          </TabsContent>

          <TabsContent value="supervisors" className="mt-4">
            <SupervisorReport data={supervisorReport} />
          </TabsContent>
        </Tabs>
      ) : (
        // Supervisor only sees operators
        <OperatorPerformanceTable operators={operatorReport?.operators || []} getRoleBadgeColor={getRoleBadgeColor} getRoleLabel={getRoleLabel} />
      )}
    </div>
  );
};

// Operator Performance Table Component
const OperatorPerformanceTable = ({ operators, getRoleBadgeColor, getRoleLabel }) => {
  // Summary stats
  const totalOps = operators.reduce((sum, op) => sum + op.total_operations, 0);
  const totalHot = operators.reduce((sum, op) => sum + op.hot_operations, 0);
  const totalCold = operators.reduce((sum, op) => sum + op.cold_operations, 0);
  const totalQty = operators.reduce((sum, op) => sum + op.total_quantity, 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{totalOps}</p>
                <p className="text-xs text-gray-500">Total Operations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{totalHot}</p>
                <p className="text-xs text-gray-500">Hot Operations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-cyan-600" />
              <div>
                <p className="text-2xl font-bold">{totalCold}</p>
                <p className="text-xs text-gray-500">Cold Operations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{totalQty}</p>
                <p className="text-xs text-gray-500">Total Quantity</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operators Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Operator Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {operators.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-semibold">Operator</th>
                    <th className="text-left p-3 font-semibold">Role</th>
                    <th className="text-center p-3 font-semibold">
                      <Flame className="w-4 h-4 inline text-red-500 mr-1" />
                      Hot
                    </th>
                    <th className="text-center p-3 font-semibold">
                      <Snowflake className="w-4 h-4 inline text-cyan-500 mr-1" />
                      Cold
                    </th>
                    <th className="text-center p-3 font-semibold">
                      <Scissors className="w-4 h-4 inline text-purple-500 mr-1" />
                      Cutting
                    </th>
                    <th className="text-center p-3 font-semibold">
                      <CircleDot className="w-4 h-4 inline text-orange-500 mr-1" />
                      Boring
                    </th>
                    <th className="text-center p-3 font-semibold">Total Ops</th>
                    <th className="text-center p-3 font-semibold">Qty</th>
                    <th className="text-center p-3 font-semibold">Job Cards</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.map((op, idx) => (
                    <tr key={op.operator_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`operator-row-${op.operator_id}`}>
                      <td className="p-3 font-medium">{op.operator_name}</td>
                      <td className="p-3">
                        <Badge className={getRoleBadgeColor(op.operator_role)}>
                          {getRoleLabel(op.operator_role)}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-semibold">
                          {op.hot_operations}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                          {op.cold_operations}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-semibold">
                          {op.cutting_operations}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold">
                          {op.boring_operations}
                        </span>
                      </td>
                      <td className="p-3 text-center font-bold">{op.total_operations}</td>
                      <td className="p-3 text-center">{op.total_quantity}</td>
                      <td className="p-3 text-center">{op.job_cards_worked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No operator activity recorded yet</p>
              <p className="text-sm">Operators will appear here when they log work</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Coordinator Performance Table Component
const CoordinatorPerformanceTable = ({ coordinators }) => {
  // Summary stats
  const totalProjects = coordinators.reduce((sum, c) => sum + c.total_projects, 0);
  const openProjects = coordinators.reduce((sum, c) => sum + c.open_projects, 0);
  const inProductionProjects = coordinators.reduce((sum, c) => sum + c.in_production_projects, 0);
  const completedProjects = coordinators.reduce((sum, c) => sum + c.completed_projects, 0);
  const closedProjects = coordinators.reduce((sum, c) => sum + c.closed_projects, 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{totalProjects}</p>
                <p className="text-xs text-gray-500">Total Job Cards</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">{openProjects}</p>
                <p className="text-xs text-gray-500">Open</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Play className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{inProductionProjects}</p>
                <p className="text-xs text-gray-500">In Production</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{completedProjects}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold">{closedProjects}</p>
                <p className="text-xs text-gray-500">Closed/Invoiced</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coordinators Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Coordinator Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {coordinators.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-semibold">Coordinator</th>
                    <th className="text-center p-3 font-semibold">Total</th>
                    <th className="text-center p-3 font-semibold">
                      <FolderOpen className="w-4 h-4 inline text-yellow-500 mr-1" />
                      Open
                    </th>
                    <th className="text-center p-3 font-semibold">
                      <Play className="w-4 h-4 inline text-purple-500 mr-1" />
                      In Production
                    </th>
                    <th className="text-center p-3 font-semibold">
                      <CheckCircle className="w-4 h-4 inline text-green-500 mr-1" />
                      Completed
                    </th>
                    <th className="text-center p-3 font-semibold">
                      <Activity className="w-4 h-4 inline text-emerald-500 mr-1" />
                      Closed / Invoiced
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {coordinators.map((coord, idx) => (
                    <tr key={coord.coordinator_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`coordinator-row-${coord.coordinator_id}`}>
                      <td className="p-3 font-medium">{coord.coordinator_name}</td>
                      <td className="p-3 text-center font-bold">{coord.total_projects}</td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 font-semibold">
                          {coord.open_projects}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-semibold">
                          {coord.in_production_projects}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold">
                          {coord.completed_projects}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                          {coord.closed_projects}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No coordinator data available</p>
              <p className="text-sm">Job cards created by coordinators will appear here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Cutting by Machine Report
const CuttingByMachineReport = ({ data }) => {
  const machines = data?.machines || [];
  return (
    <div className="space-y-4" data-testid="cutting-by-machine-report">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{machines.length}</p>
                <p className="text-xs text-gray-500">Machines Used</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{data?.total_quantity ?? 0}</p>
                <p className="text-xs text-gray-500">Total Cuts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold">{data?.total_job_cards ?? 0}</p>
                <p className="text-xs text-gray-500">Job Cards Worked</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Cutting by Machine
          </CardTitle>
        </CardHeader>
        <CardContent>
          {machines.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="p-3">Machine Code</th>
                    <th className="p-3">Name</th>
                    <th className="p-3 text-center">Total Cuts</th>
                    <th className="p-3 text-center">Job Cards</th>
                    <th className="p-3">Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {machines.map((m, idx) => (
                    <tr key={m.machine_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`cutting-machine-row-${m.machine_number || m.machine_id}`}>
                      <td className="p-3 font-mono font-medium">
                        {m.machine_number || <span className="italic text-gray-400">(deleted)</span>}
                        {m.machine_active === false && <Badge className="ml-2 bg-gray-200 text-gray-600">inactive</Badge>}
                      </td>
                      <td className="p-3">{m.machine_name || '-'}</td>
                      <td className="p-3 text-center font-bold text-purple-600">{m.total_quantity}</td>
                      <td className="p-3 text-center">{m.job_card_count}</td>
                      <td className="p-3 text-xs text-gray-500">{m.last_used ? new Date(m.last_used).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Wrench className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No cutting logged against a machine in this period.</p>
              <p className="text-sm mt-1 text-gray-600">
                Cutting machines are set up — operators pick one (CM-1…CM-6) when submitting cutting details,
                and totals appear here from the next entry onward.
              </p>
              <p className="text-xs mt-1">Earlier cutting was recorded before machines existed, so it can't be attributed.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Pressing by Type (HOT/COLD) Report
const PressingByTypeReport = ({ data }) => {
  const byOp = data?.by_operator || [];
  return (
    <div className="space-y-4" data-testid="pressing-by-type-report">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{data?.hot_count ?? 0}</p>
                <p className="text-xs text-gray-500">HOT operations</p>
                <p className="text-xs text-gray-400">Qty: {data?.hot_quantity ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-cyan-600" />
              <div>
                <p className="text-2xl font-bold text-cyan-700">{data?.cold_count ?? 0}</p>
                <p className="text-xs text-gray-500">COLD operations</p>
                <p className="text-xs text-gray-400">Qty: {data?.cold_quantity ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{(data?.hot_count ?? 0) + (data?.cold_count ?? 0)}</p>
                <p className="text-xs text-gray-500">Combined HOT+COLD</p>
                <p className="text-xs text-gray-400">Qty: {(data?.hot_quantity ?? 0) + (data?.cold_quantity ?? 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Pressing by Operator (HOT / COLD)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {byOp.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="p-3">Operator</th>
                    <th className="p-3 text-center">HOT</th>
                    <th className="p-3 text-center">COLD</th>
                    <th className="p-3 text-center">HOT Qty</th>
                    <th className="p-3 text-center">COLD Qty</th>
                    <th className="p-3 text-center">Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {byOp.map((op, idx) => (
                    <tr key={op.operator_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`pressing-op-row-${op.operator_id}`}>
                      <td className="p-3 font-medium">{op.operator_name}</td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 h-8 rounded-full bg-red-100 text-red-700 font-semibold">
                          {op.hot_count}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 h-8 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                          {op.cold_count}
                        </span>
                      </td>
                      <td className="p-3 text-center text-red-600 font-bold">{op.hot_quantity}</td>
                      <td className="p-3 text-center text-cyan-600 font-bold">{op.cold_quantity}</td>
                      <td className="p-3 text-center font-bold">{op.total_quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Layers className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No pressing operations logged for this period.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Cutting Machine Heatmap (machine × day grid)
const CuttingMachineHeatmap = ({ data }) => {
  const machines = data?.machines || [];
  const dates = data?.dates || [];
  const max = Math.max(1, data?.max_daily_quantity || 0);

  // Helper: format date as short label
  const formatDay = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };
  const weekdayLabel = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short' });
  };

  // Colour scale (Tailwind sky tints) based on quantity / max
  const cellStyle = (qty) => {
    if (!qty) return { background: 'rgb(243 244 246)', color: '#9ca3af' }; // gray-100 / 400
    const ratio = Math.min(1, qty / max);
    // Interpolate between sky-50 and sky-700
    const r = Math.round(240 + (3 - 240) * ratio);   // 240 -> 3
    const g = Math.round(249 + (105 - 249) * ratio); // 249 -> 105
    const b = Math.round(255 + (161 - 255) * ratio); // 255 -> 161
    const textColor = ratio > 0.55 ? '#ffffff' : '#0c4a6e';
    return { background: `rgb(${r}, ${g}, ${b})`, color: textColor };
  };

  return (
    <div className="space-y-4" data-testid="cutting-heatmap-report">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Cutting Machine Utilisation Heatmap
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Each cell shows the total cuts logged on that machine for that day. Darker = busier. Default window: last 7 days.
          </p>
        </CardHeader>
        <CardContent>
          {machines.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Wrench className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No cutting machines configured yet.</p>
              <p className="text-sm">Add cutting machines from Admin → Machines.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="border-separate border-spacing-1 text-xs">
                <thead>
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-white z-10 min-w-[140px]">Machine</th>
                    {dates.map(d => (
                      <th key={d} className="p-1 text-center min-w-[64px] font-normal text-gray-600">
                        <div className="font-semibold">{formatDay(d)}</div>
                        <div className="text-[10px] text-gray-400">{weekdayLabel(d)}</div>
                      </th>
                    ))}
                    <th className="p-2 text-center min-w-[70px] font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {machines.map(m => (
                    <tr key={m.machine_id} data-testid={`heatmap-row-${m.machine_number || m.machine_id}`}>
                      <td className="p-2 sticky left-0 bg-white z-10 font-medium">
                        <div className="font-mono">{m.machine_number || <span className="italic text-gray-400">(deleted)</span>}</div>
                        <div className="text-[11px] text-gray-500">{m.machine_name}</div>
                        {m.is_active === false && <Badge className="mt-1 bg-gray-200 text-gray-600">inactive</Badge>}
                      </td>
                      {dates.map(d => {
                        const cell = m.daily?.[d] || { quantity: 0, count: 0 };
                        const style = cellStyle(cell.quantity);
                        return (
                          <td
                            key={d}
                            className="text-center rounded-md font-semibold tabular-nums"
                            style={{ ...style, minWidth: 64, height: 44 }}
                            title={`${m.machine_number || m.machine_name} • ${formatDay(d)}\nCuts: ${cell.quantity}\nLogs: ${cell.count}`}
                            data-testid={`heatmap-cell-${m.machine_number || m.machine_id}-${d}`}
                          >
                            {cell.quantity || ''}
                          </td>
                        );
                      })}
                      <td className="p-2 text-center font-bold text-purple-700 bg-purple-50 rounded-md">
                        {m.total_quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Legend */}
              <div className="flex items-center gap-3 mt-4 text-xs text-gray-600">
                <span>Less</span>
                <div className="flex gap-1">
                  {[0, 0.15, 0.35, 0.6, 0.85, 1].map((r, i) => {
                    const qty = Math.round(max * r);
                    const s = cellStyle(qty);
                    return (
                      <div key={i} className="w-6 h-4 rounded" style={s} title={`~${qty} cuts`} />
                    );
                  })}
                </div>
                <span>More</span>
                <span className="ml-4 text-gray-400">Peak: {max} cuts/day</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Coordinator Tasks Report
const CoordinatorTasksReport = ({ data }) => {
  const performers = data?.performers || [];
  const totals = data?.totals || {};
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-4" data-testid="coord-tasks-report">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-700">{totals.total ?? 0}</p>
            <p className="text-xs text-gray-500">Total tasks completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-700">{totals.by_coordinator_count ?? 0}</p>
            <p className="text-xs text-gray-500">By Coordinator</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-700">{totals.by_client_count ?? 0}</p>
            <p className="text-xs text-gray-500">By Client</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Drawing / Cutting / Recheck</p>
            <p className="text-base font-semibold">
              {totals.drawing ?? 0} · {totals.cutting_list ?? 0} · {totals.recheck ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-700">{performers.length}</p>
            <p className="text-xs text-gray-500">Distinct performers</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Coordinator Tasks — by Performer
          </CardTitle>
        </CardHeader>
        <CardContent>
          {performers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="p-3">Performer</th>
                    <th className="p-3 text-center">Drawing</th>
                    <th className="p-3 text-center">Cutting List</th>
                    <th className="p-3 text-center">Re-check</th>
                    <th className="p-3 text-center">Total</th>
                    <th className="p-3 text-right">Job Cards</th>
                  </tr>
                </thead>
                <tbody>
                  {performers.map((p) => {
                    const isOpen = expanded === `${p.role}::${p.name}`;
                    return (
                      <React.Fragment key={`${p.role}::${p.name}`}>
                        <tr className="hover:bg-gray-50" data-testid={`coord-task-row-${p.name}`}>
                          <td className="p-3 font-medium">
                            <div className="flex items-center gap-2">
                              <span>{p.name}</span>
                              <Badge className={p.role === 'client' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                                {p.role}
                              </Badge>
                            </div>
                          </td>
                          <td className="p-3 text-center">{p.drawing}</td>
                          <td className="p-3 text-center">{p.cutting_list}</td>
                          <td className="p-3 text-center">{p.recheck}</td>
                          <td className="p-3 text-center font-bold text-blue-700">{p.total}</td>
                          <td className="p-3 text-right">
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => setExpanded(isOpen ? null : `${p.role}::${p.name}`)}
                              data-testid={`view-jobcards-${p.name}`}
                            >
                              {isOpen ? 'Hide' : `View ${(p.job_cards || []).length}`}
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={6} className="bg-gray-50 p-3">
                              <div className="text-xs text-gray-600 mb-2">Job cards & tasks completed:</div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs border bg-white">
                                  <thead>
                                    <tr className="border-b">
                                      <th className="p-2 text-left">JC #</th>
                                      <th className="p-2 text-left">Customer</th>
                                      <th className="p-2 text-left">Task</th>
                                      <th className="p-2 text-left">When</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(p.job_cards || []).map((jc, i) => (
                                      <tr key={i} className="border-b">
                                        <td className="p-2 font-mono">{jc.job_card_number}</td>
                                        <td className="p-2">{jc.customer}</td>
                                        <td className="p-2 capitalize">{(jc.task_type || '').replace(/_/g, ' ')}</td>
                                        <td className="p-2 text-gray-500">
                                          {jc.completed_at ? new Date(jc.completed_at).toLocaleString() : '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No coordinator tasks completed in this period.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Supervisor KPIs: approvals, rejections, pending queue, avg review time
const SupervisorReport = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        <CheckCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p>Supervisor report unavailable.</p>
      </div>
    );
  }
  const supervisors = data.supervisors || [];
  const totalApprovals = supervisors.reduce((s, x) => s + x.approvals, 0);
  const totalRejections = supervisors.reduce((s, x) => s + x.rejections, 0);
  return (
    <div className="space-y-4" data-testid="supervisor-report">
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{totalApprovals}</p>
                <p className="text-xs text-gray-500">Approvals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{totalRejections}</p>
                <p className="text-xs text-gray-500">Rejections</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-2xl font-bold">{data.pending_now ?? 0}</p>
                <p className="text-xs text-gray-500">Pending Review Now</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Supervisor Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {supervisors.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="p-3">Supervisor</th>
                    <th className="p-3 text-center">Approved</th>
                    <th className="p-3 text-center">Rejected</th>
                    <th className="p-3 text-center">Total Reviews</th>
                    <th className="p-3 text-center">Avg Review Time</th>
                  </tr>
                </thead>
                <tbody>
                  {supervisors.map((s, idx) => (
                    <tr key={s.supervisor_name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`supervisor-row-${idx}`}>
                      <td className="p-3 font-medium">{s.supervisor_name}</td>
                      <td className="p-3 text-center font-bold text-green-600">{s.approvals}</td>
                      <td className="p-3 text-center font-bold text-red-600">{s.rejections}</td>
                      <td className="p-3 text-center">{s.total_reviews}</td>
                      <td className="p-3 text-center text-gray-600">
                        {s.avg_review_hours != null ? `${s.avg_review_hours} h` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No supervisor reviews recorded in this period.</p>
              <p className="text-sm mt-1">Review tracking started recently — approvals before that aren't counted.</p>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-gray-400">
        Avg review time = time from "sent to supervisor" to approval. Tracking begins from the date this report was deployed.
      </p>
    </div>
  );
};

export default PerformanceReports;
